import { ForeignLanguageBlock } from './blocks/ForeignLanguageBlock';
import { Block } from '@components/core-ui/Block';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { UploadProvider, useUploadContext } from '@components/Files/UploadProvider';
import { S3SignedUrlUploader } from 'app/features/uploads/S3SignedUrlUploader';
import { useJobUploadParamsMutation } from 'app/features/uploads/useJobUploadParamsMutation';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { Spinner } from '@components/core-ui/spinners/Spinner';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { EErrorMessages } from 'app/types/errorMessages';
import { cn } from '@helpers/cn';
import { isAssemblyKeytermsEnabled } from '@helpers/featureFlags/isAssemblyKeytermsEnabled';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import { sanitizeFileName } from '@helpers/sanitizeFileName';
import { useAPI } from '@providers/useAPI';
import { AttorneysBlock } from '@pages/User/NewJobPage/components/Content/blocks/AttorneysBlock';
import { usePageContext } from '@pages/User/NewJobPage/providers/PageProvider';
import { useSaveJob } from '@pages/User/NewJobPage/hooks/useSaveJob';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { routes } from '@routes';
import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateJobBodyDto, JobAttachmentResponseDto, JobDtoAttachment, MediaService, PayService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { createFetchJobAttachmentQueryFn } from 'app/queries/jobs/createFetchJobAttachmentQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { EPermission } from 'app/types/enums/EPermission';
import { mapServerMediaItemsToFiles } from '@pages/User/NewJobPage/helpers/mapServerMediaItemsToFiles';
import { mergeFilesFromServerAndNew } from '@pages/User/NewJobPage/helpers/mergeFilesFromServerAndNew';
import { getNewJobFilesSubmitReadiness } from '@pages/User/NewJobPage/helpers/getNewJobFilesSubmitReadiness';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import sanitize from 'sanitize-filename';
import { PaymentIntentBox } from '../../modals/PaymentIntentBox';
import { AssigneeBlock } from './blocks/AssigneeBlock';
import { AudioFilesBlock } from './blocks/AudioFiles';
import { DeadlineBlock } from './blocks/DeadlineBlock';
import { KeyTermsBlock } from './blocks/KeyTermsBlock';
import { LayoutBlock } from './blocks/LayoutBlock';
import { NotesBlock } from './blocks/NotesBlock';
import { RepositoryBlock } from './blocks/RepositoryBlock';
import { ScheduleBlock } from './blocks/ScheduleBlock';
import { TagsBlock } from './blocks/TagsBlock';
import { Buttons } from './components/Buttons';
import { FormGenericErrors } from './components/FormGenericErrors';
import { JobLoadError } from '../JobLoadError';
import { UploadAttachmentsBlock } from './components/UploadAttachmentsBlock';
import { useMediaAnalysisPolling } from 'app/hooks/useMediaAnalysisPolling';
import { hasSpecialCharacters } from '@helpers/hasSpecialCharacters';
import { FormWrapper } from './styles/FormWrapper';
import { Input } from '@components/core-ui/form-elements/Input';
import { StyledForm } from './styles/StyledForm';

const ContentInner = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization, workspace } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const navigate = useNavigate();
  const { files, mergeWithServerFiles, setExtra, startUpload } = useUploadContext();

  const {
    jobId,
    isInitialLoading,
    isSaving,
    setIsSaving,
    form,
    setForm,
    isNew,
    job,
    setJob,
    serverMediaItems,
    ensureDraftJobBeforeUpload,
  } = usePageContext();

  const showSchedule = form.item.status && jobIsScheduled(form.item?.status);

  const [paymentIntent, setPaymentIntent] = useState<{ url: string; id: string }>({
    url: '',
    id: '',
  });
  const [errorJobNameMessage, setErrorJobNameMessage] = useState('');
  const [noticeExtractionRunning, setNoticeExtractionRunning] = useState(false);
  const [attachmentUploadRunning, setAttachmentUploadRunning] = useState(false);
  const createMediaStartedRef = useRef<Set<string>>(new Set());

  const { mutateAsync: createMedia } = useMutation({
    mutationFn: (requestBody: Parameters<typeof MediaService.createMedia>[0]['requestBody']) =>
      call(() => MediaService.createMedia({ requestBody })),
  });

  const updateMediaWeightMutation = useMutation({
    mutationFn: ({ mediaId, weight }: { mediaId: string; weight: number }) =>
      call(() => MediaService.updateMedia({ jobId, mediaId, requestBody: { weight } })),
    onError: notifyApiCallError,
  });

  const payMutation = useMutation({
    mutationFn: () => call(() => PayService.pay({ jobId, requestBody: {} })),
    onSuccess: () => {
      navigate(routes.jobsList.make(organization?.slug ?? '', workspace?.slug ?? ''));
      setIsSaving(false);
    },
    onError: (error) => {
      notifyApiCallError(error);
      setIsSaving(false);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: jobsKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: jobsKeys.detail(jobId), refetchType: 'none' }),
      ]),
  });

  useMediaAnalysisPolling({ files, setExtra, jobId });

  const updateJobAttachment = useCallback(
    (attachment: JobAttachmentResponseDto) => {
      const nextAttachment: JobDtoAttachment = {
        id: attachment.id,
        fileName: attachment.filename,
        path: attachment.filename,
        fileSize: attachment.fileSize,
        extracted: attachment.extracted,
        attachmentType: attachment.attachmentType,
      };

      setJob((currentJob) => {
        if (!currentJob) {
          return currentJob;
        }

        const attachmentIndex = currentJob.attachments.findIndex(
          (item) =>
            item.id === attachment.id || item.path === attachment.filename || item.fileName === attachment.filename,
        );

        if (attachmentIndex === -1) {
          return {
            ...currentJob,
            attachments: [...currentJob.attachments, nextAttachment],
          };
        }

        return {
          ...currentJob,
          attachments: currentJob.attachments.map((item, index) =>
            index === attachmentIndex ? { ...item, ...nextAttachment } : item,
          ),
        };
      });
    },
    [setJob],
  );

  useEffect(() => {
    if (!serverMediaItems.length) {
      return;
    }
    mergeWithServerFiles(mapServerMediaItemsToFiles(serverMediaItems));
  }, [serverMediaItems, mergeWithServerFiles]);

  const handleCompletedUpload = useCallback(
    (f: (typeof files)[0]) => {
      if (createMediaStartedRef.current.has(f.meta.id)) {
        return;
      }
      createMediaStartedRef.current.add(f.meta.id);
      setExtra(f.meta.id, { analysisRequested: true, analysisStatus: 'progress' });
      const weight = files.findIndex((other) => other.meta.id === f.meta.id);
      const fileName = sanitizeFileName(f.meta.name);
      const uploadJobId = String(f.meta.extra?.jobId || jobId);

      createMedia({
        jobId: uploadJobId,
        fileName,
        fileSize: String(f.file?.size ?? f.meta.size ?? '0'),
        weight,
      })
        .then((response) => {
          if (!response || !response.id) {
            setExtra(f.meta.id, { analysisStatus: 'error' });
            return;
          }
          setExtra(f.meta.id, { mediaId: response.id });
        })
        .catch((e) => {
          Sentry.captureMessage(`Error creating media for job ${uploadJobId}`, { extra: { f, e, jobId: uploadJobId } });
          Sentry.captureException(e);
          setExtra(f.meta.id, { analysisStatus: 'error' });
        });
    },
    [createMedia, files, jobId, setExtra],
  );

  useLayoutEffect(() => {
    files.forEach((f) => {
      const isLocalUploadedFile = f.meta.status === 'done' && Boolean(f.file);
      if (!isLocalUploadedFile) {
        return;
      }
      if (f.meta.extra?.mediaId || f.meta.extra?.analysisRequested) {
        return;
      }
      handleCompletedUpload(f);
    });
  }, [files, handleCompletedUpload]);

  useEffect(() => {
    files.forEach((f) => {
      if (f.meta.status !== 'external') {
        return;
      }
      const mediaId = String(f.meta.extra?.mediaId ?? '');
      if (!mediaId) {
        return;
      }
      if (f.meta.extra?.analysisStatus === 'done' || f.meta.extra?.analysisStatus === 'error') {
        return;
      }
      if (f.meta.extra?.analysisPollStarted) {
        return;
      }
      setExtra(f.meta.id, { analysisPollStarted: true, analysisStatus: 'progress' });
    });
  }, [files, setExtra]);

  useEffect(() => {
    files.forEach((f) => {
      if (f.meta.status === 'queued' && f.meta.extra?.jobId) {
        startUpload(f.meta.id);
      }
    });
  }, [files, startUpload]);

  useEffect(() => {
    let cancelled = false;
    const attachJobToQueued = async () => {
      const queuedWithoutJob = files.filter((f) => f.meta.status === 'queued' && !f.meta.extra?.jobId);
      if (!queuedWithoutJob.length) {
        return;
      }
      const ensuredJobId = await ensureDraftJobBeforeUpload();
      if (!ensuredJobId || cancelled) {
        return;
      }
      queuedWithoutJob.forEach((f) => {
        setExtra(f.meta.id, { jobId: ensuredJobId });
      });
    };
    void attachJobToQueued();
    return () => {
      cancelled = true;
    };
  }, [files, ensureDraftJobBeforeUpload, setExtra]);

  const saveFilesOrder = () => {
    const orderedActiveFiles = files.filter((item) => item.meta.status !== 'removed');
    orderedActiveFiles.forEach((item, index) => {
      const mediaId = item.meta.extra?.mediaId ?? item.meta.id;
      const canPersistWeight = item.meta.status === 'done' || item.meta.status === 'external';
      if (jobId && mediaId && canPersistWeight) {
        updateMediaWeightMutation.mutate({ mediaId, weight: index });
      }
    });
  };

  const saveJob = useSaveJob(saveFilesOrder);

  const { activeFiles, isReady: filesReadyForSubmit } = getNewJobFilesSubmitReadiness(files);

  const onSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (attachmentUploadRunning) {
      toast.error('Attachment upload is still running');
      return;
    }
    if (noticeExtractionRunning) {
      toast.error('Notice extraction is still running');
      return;
    }
    const result = await saveJob('JOB_STATUS_DRAFT');
    if (!result) {
      return;
    }
    if (!filesReadyForSubmit) {
      setIsSaving(false);
      return;
    }
    saveFilesOrder();
    openModal(ModalNamesEnum.PriceModal, {
      onCancel: onModalCancel,
      setPaymentIntent,
      paymentIntent,
      onSuccess: handleTranscribe,
      jobId,
    });
  };

  const onModalCancel = () => {
    closeModal();
    setIsSaving(false);
  };

  function handleTranscribe() {
    setIsSaving(true);
    closeModal();
    payMutation.mutate();
  }

  const handleInputChange = async (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = evt.target;
    setForm({
      ...form,
      item: { ...(form.item as CreateJobBodyDto), [name]: value },
    });
  };

  useEffect(() => {
    setErrorJobNameMessage('');

    if ((form?.item?.name || '').startsWith(' ')) {
      setErrorJobNameMessage(EErrorMessages.JOB_NAME_STARTS_WITH_SPACE);
      return;
    }

    if ((form?.item?.name || '').endsWith(' ')) {
      setErrorJobNameMessage(EErrorMessages.JOB_NAME_ENDS_WITH_SPACE);
      return;
    }

    if (sanitize(form?.item?.name || '') !== (form?.item?.name || '')) {
      setErrorJobNameMessage(EErrorMessages.WRONG_JOB_NAME_SYMBOLS);
      return;
    }
    if (hasSpecialCharacters(form?.item?.name || '')) {
      setErrorJobNameMessage(EErrorMessages.WRONG_JOB_NAME_SYMBOLS);
      return;
    }

    if ((form?.item?.name || '').length > 255) {
      setErrorJobNameMessage(EErrorMessages.JOB_NAME_IS_TOO_LONG);
    }
  }, [form?.item?.name]);

  const newUploadCount = files.filter((f) => f.meta.status !== 'external').length;
  const activeJobId = job?.id ?? jobId;
  const showKeyTerms = isAssemblyKeytermsEnabled(organization?.featureFlags);

  return (
    <>
      {isInitialLoading ? <Spinner overlay={true} /> : null}
      <LinearProgressLoader active={isSaving} />

      <FormWrapper aria-busy={isSaving} className={cn(isSaving && 'pointer-events-none')}>
        <fieldset disabled={isSaving} className="contents">
          <FormGenericErrors />

          <Block title="Provide a unique title for your job">
            <Input
              onChange={handleInputChange}
              name="name"
              label="Title"
              value={form.item?.name || ''}
              required={true}
              data-testid="jobNameInput"
            />
            {errorJobNameMessage ? <ErrorMessage error={errorJobNameMessage} /> : <></>}
          </Block>

          {!!organization?.featureFlags?.ENABLE_RUSHED_JOBS && (
            <Block title="Rush Job">
              <div />
              <Switch
                checked={!!form.item?.rushed}
                onChange={() =>
                  setForm({
                    ...form,
                    item: { ...form.item, rushed: !form.item?.rushed } as CreateJobBodyDto,
                  })
                }
              />
            </Block>
          )}

          <ForeignLanguageBlock />

          {form.item.meetingId || (form.item.status && jobIsScheduled(form.item.status)) ? null : <AudioFilesBlock />}

          {organization?.id && workspace?.id ? (
            <Block title="Upload Attachments">
              <UploadAttachmentsBlock
                organizationId={String(organization.id)}
                organizationSlug={organization.slug}
                workspaceSlug={workspace?.slug ?? ''}
                noticeExtractorEnabled={organization.featureFlags?.NOTICE_EXTRACTOR_ENABLED === true}
                onNoticeExtractionRunningChange={setNoticeExtractionRunning}
                onAttachmentUploadRunningChange={setAttachmentUploadRunning}
                attachments={job?.attachments ?? []}
                ensureDraftJobBeforeUpload={ensureDraftJobBeforeUpload}
                getJobId={() => activeJobId}
                onRefreshAttachment={(attachmentId) =>
                  queryClient.fetchQuery({
                    queryKey: jobsKeys.attachment(activeJobId, workspace.id, attachmentId),
                    queryFn: createFetchJobAttachmentQueryFn(call, {
                      dontRedirectOnError: true,
                      onError: () => undefined,
                    }),
                  })
                }
                onAttachmentChange={updateJobAttachment}
              />
            </Block>
          ) : null}

          {showKeyTerms ? (
            <KeyTermsBlock noticeExtractionRunning={noticeExtractionRunning} attachments={job?.attachments || []} />
          ) : null}

          {(isNew && !newUploadCount && organization?.billingAccount?.ActiveSubscription) || form.item.meetingId ? (
            <ScheduleBlock />
          ) : null}

          <LayoutBlock />

          <Restricted to={EPermission.viewRepositories}>
            <RepositoryBlock />
          </Restricted>

          <NotesBlock />
          <TagsBlock />
          <DeadlineBlock />
          <AssigneeBlock />

          {showSchedule && organization?.featureFlags.ATTORNEY_VIEW_AVAILABLE ? <AttorneysBlock /> : null}
        </fieldset>
      </FormWrapper>

      <StyledForm className="form" method="post" onSubmit={onSubmit}>
        <Buttons
          saveFilesOrder={saveFilesOrder}
          filesLength={activeFiles.length}
          errorJobNameMessage={errorJobNameMessage}
          noticeExtractionRunning={noticeExtractionRunning}
          attachmentUploadRunning={attachmentUploadRunning}
        />
      </StyledForm>
      <PaymentIntentBox paymentIntent={paymentIntent} />
    </>
  );
};

export const Content = () => {
  const generateUploadParams = useJobUploadParamsMutation();
  const { jobId, isLoadError } = usePageContext();

  if (isLoadError) {
    return <JobLoadError />;
  }

  return (
    <UploadProvider
      config={{
        mode: 'deferred',
        uploader: S3SignedUrlUploader(generateUploadParams),
        extra: { jobId },
        mergeServerFiles: mergeFilesFromServerAndNew,
      }}
    >
      <ContentInner />
    </UploadProvider>
  );
};
